import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Minus, Plus, X, Tv } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { apiClient } from '@/services/api';
import { formatCents } from '@/utils/cn';
import type { Plan, BillingCycle } from '@/types/plan';
import { calcPlanTotalCents } from '@/types/plan';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: Plan;
  currentTvCount: number;
  currentBillingCycle: BillingCycle;
  isTrialing: boolean;
}

/** Modal pra ajustar quantidade de TVs. Mostra calculadora ao vivo. */
export function AdjustTvsModal({
  open,
  onOpenChange,
  plan,
  currentTvCount,
  currentBillingCycle,
  isTrialing,
}: Props) {
  const qc = useQueryClient();
  const [tvCount, setTvCount] = useState<number>(currentTvCount);

  // Usa o endpoint PATCH dedicado (não exige PaymentMethod — funciona em trial).
  const adjustMutation = useMutation({
    mutationFn: () => apiClient.updateSubscription({ tv_count: tvCount }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscription'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      onOpenChange(false);
    },
  });

  const current = calcPlanTotalCents(plan, currentTvCount, currentBillingCycle);
  const next = calcPlanTotalCents(plan, tvCount, currentBillingCycle);
  const delta = next - current;

  const firstCents = currentBillingCycle === 'monthly'
    ? plan.first_tv_monthly_cents
    : Math.round(plan.first_tv_annual_cents / 12);
  const addCents = currentBillingCycle === 'monthly'
    ? plan.price_per_tv_monthly_cents
    : Math.round(plan.price_per_tv_annual_cents / 12);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl bg-background p-6 shadow-2xl">
          <div className="flex items-start justify-between mb-4">
            <div>
              <Dialog.Title className="text-xl font-bold flex items-center gap-2">
                <Tv className="h-5 w-5" />
                Ajustar TVs
              </Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground mt-1">
                Plano <strong>{plan.name}</strong> · {currentBillingCycle === 'monthly' ? 'Mensal' : 'Anual'}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button className="rounded-md p-1 hover:bg-muted" aria-label="Fechar">
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Stepper */}
          <div className="text-center mb-2 text-sm text-muted-foreground">Quantidade de TVs</div>
          <div className="flex items-center justify-center gap-3 mb-6">
            <button
              type="button"
              onClick={() => setTvCount(t => Math.max(1, t - 1))}
              className="h-12 w-12 rounded-lg border-2 border-border hover:bg-muted flex items-center justify-center"
              aria-label="Diminuir"
            ><Minus className="h-5 w-5" /></button>
            <input
              type="number"
              min={1}
              max={999}
              value={tvCount}
              onChange={(e) => setTvCount(Math.max(1, Math.min(999, parseInt(e.target.value) || 1)))}
              className="w-24 text-center font-bold text-4xl bg-transparent border-2 border-border rounded-lg py-2 focus:border-brand-600 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setTvCount(t => Math.min(999, t + 1))}
              className="h-12 w-12 rounded-lg border-2 border-border hover:bg-muted flex items-center justify-center"
              aria-label="Aumentar"
            ><Plus className="h-5 w-5" /></button>
          </div>

          {/* Breakdown */}
          <div className="rounded-lg bg-muted/50 p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">1ª TV (taxa base)</span>
              <span className="font-medium">{formatCents(firstCents)}</span>
            </div>
            {tvCount > 1 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{tvCount - 1}× TV adicional</span>
                <span className="font-medium">{formatCents((tvCount - 1) * addCents)}</span>
              </div>
            )}
            <div className="border-t border-border pt-2 flex justify-between items-baseline">
              <span className="font-semibold">Total</span>
              <span className="text-2xl font-bold">{formatCents(next)}<span className="text-xs text-muted-foreground font-normal">/mês</span></span>
            </div>
          </div>

          {/* Delta vs atual */}
          {tvCount !== currentTvCount && (
            <div className="mt-3 text-center text-sm">
              {delta > 0 ? (
                <span className="text-amber-700">
                  +{formatCents(delta)}/mês comparado ao atual ({currentTvCount} TV{currentTvCount > 1 ? 's' : ''})
                </span>
              ) : (
                <span className="text-emerald-700">
                  Economia de {formatCents(Math.abs(delta))}/mês comparado ao atual
                </span>
              )}
            </div>
          )}

          {isTrialing && (
            <div className="mt-3">
              <Badge variant="warning" className="text-xs">
                Trial — a cobrança só começa após a ativação
              </Badge>
            </div>
          )}

          {adjustMutation.isError && (
            <div className="mt-3 text-sm text-red-600 text-center">
              Erro ao ajustar TVs. Tente novamente em alguns segundos.
            </div>
          )}

          <div className="mt-6 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button
              variant="gradient"
              loading={adjustMutation.isPending}
              disabled={tvCount === currentTvCount}
              onClick={() => adjustMutation.mutate()}
            >
              Salvar ({tvCount} TV{tvCount > 1 ? 's' : ''})
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
